import React, { useState, useEffect } from 'react';
import './OrderDatePicker.css';

const OrderDatePicker = ({ selectedDate, onDateChange, availableDates = [] }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  // Create Set of available dates for O(1) lookup
  const availableDatesSet = new Set(availableDates);

  // Get the current date for navigation
  const today = new Date();
  
  // Format date for display
  const formatDisplayDate = (date) => {
    if (!date) return 'Todas las fechas';
    // Parse date string in local timezone to avoid timezone issues
    const [year, month, day] = date.split('-').map(Number);
    const d = new Date(year, month - 1, day); // month is 0-indexed
    return d.toLocaleDateString('es-ES', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Generate calendar days
  const generateCalendarDays = (month) => {
    const year = month.getFullYear();
    const monthIndex = month.getMonth();
    
    // First day of the month
    const firstDay = new Date(year, monthIndex, 1);
    // Last day of the month
    const lastDay = new Date(year, monthIndex + 1, 0);
    
    // Get the day of week for first day (0 = Sunday, 1 = Monday, etc.)
    const firstDayOfWeek = firstDay.getDay();
    
    // Array to store all days
    const days = [];
    
    // Add empty cells for days before month starts
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(year, monthIndex, day);
      const dateString = date.toISOString().split('T')[0];
      days.push({
        date,
        dateString,
        day,
        isAvailable: availableDatesSet.has(dateString),
        isSelected: selectedDate === dateString,
        isToday: dateString === today.toISOString().split('T')[0]
      });
    }
    
    return days;
  };

  // Navigate to previous month
  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  // Navigate to next month
  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  // Handle date selection
  const handleDateSelect = (dayData) => {
    if (dayData.isAvailable) {
      onDateChange(dayData.dateString);
      setIsCalendarOpen(false);
    }
  };

  // Toggle calendar visibility
  const toggleCalendar = () => {
    setIsCalendarOpen(!isCalendarOpen);
  };

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isCalendarOpen && !event.target.closest('.order-date-picker')) {
        setIsCalendarOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCalendarOpen]);

  // Get month/year display
  const monthYearDisplay = currentMonth.toLocaleDateString('es-ES', { 
    year: 'numeric', 
    month: 'long' 
  });

  return (
    <div className="order-date-picker">
      <div className="date-picker-input" onClick={toggleCalendar}>
        <span className="selected-date">
          {formatDisplayDate(selectedDate)}
        </span>
        <span className="dropdown-arrow">
          {isCalendarOpen ? '▲' : '▼'}
        </span>
      </div>

      {isCalendarOpen && (
        <div className="calendar-dropdown">
          <div className="calendar-header">
            <button 
              type="button" 
              className="nav-button" 
              onClick={goToPreviousMonth}
            >
              ‹
            </button>
            <span className="month-year">{monthYearDisplay}</span>
            <button 
              type="button" 
              className="nav-button" 
              onClick={goToNextMonth}
            >
              ›
            </button>
          </div>

          <div className="calendar-grid">
            <div className="weekdays">
              {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map(day => (
                <div key={day} className="weekday">{day}</div>
              ))}
            </div>

            <div className="days-grid">
              {generateCalendarDays(currentMonth).map((dayData, index) => (
                <div key={index} className="day-cell">
                  {dayData && (
                    <button
                      type="button"
                      className={`day-button ${
                        dayData.isSelected ? 'selected' : ''
                      } ${
                        dayData.isAvailable ? 'available' : 'disabled'
                      } ${
                        dayData.isToday ? 'today' : ''
                      }`}
                      onClick={() => handleDateSelect(dayData)}
                      disabled={!dayData.isAvailable}
                      title={
                        dayData.isAvailable 
                          ? `${dayData.day} de ${monthYearDisplay} - Hay pedidos`
                          : `${dayData.day} de ${monthYearDisplay} - Sin pedidos`
                      }
                    >
                      {dayData.day}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="calendar-footer">
            <div className="footer-actions">
              <button 
                type="button" 
                className="clear-filter-btn"
                onClick={() => {
                  onDateChange('');
                  setIsCalendarOpen(false);
                }}
              >
                Todas las fechas
              </button>
            </div>
            <small>
              Solo se muestran fechas con pedidos • 
              {availableDates.length} días disponibles
            </small>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDatePicker;